import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { FileSearch, MoreHorizontal, Loader2 } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useMatters, getStageLabel, useMatterPartyNames, getPartyName } from "@/hooks/use-supabase-queries";
import { formatDistanceToNow } from "date-fns";
import { useIsMobile } from "@/hooks/use-mobile";

// Helper component to look up client name per matter
function ClientNameCell({ matterId, clientId }: { matterId: string; clientId: string | null }) {
  const { data: partyNames } = useMatterPartyNames(matterId);
  return <>{getPartyName(partyNames, clientId)}</>;
}

const statusConfig = {
  active: { label: "Active", variant: "default" as const },
  paused: { label: "Paused", variant: "secondary" as const },
  completed: { label: "Completed", variant: "outline" as const },
  archived: { label: "Archived", variant: "destructive" as const },
};

interface ActiveMattersTableProps {
  onViewMatter?: (matterId: string) => void;
}

export function ActiveMattersTable({ onViewMatter }: ActiveMattersTableProps) {
  const { data: matters, isLoading } = useMatters();
  const isMobile = useIsMobile();
  const stageBadgeClass = "inline-flex max-w-full items-center justify-center whitespace-normal break-words rounded-full bg-secondary/50 px-2.5 py-1 text-center text-[11px] font-medium leading-4";

  if (isLoading) {
    return (
      <div className="rounded-lg border border-border bg-card p-8 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const activeMatterCount = matters?.filter(m => m.status === 'active').length || 0;
  const renderActionMenu = (matterId: string) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          aria-label="More actions"
          onClick={(event) => event.stopPropagation()}
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-popover border-border">
        <DropdownMenuItem onClick={() => onViewMatter?.(matterId)}>View Details</DropdownMenuItem>
        <DropdownMenuItem>Update Stage</DropdownMenuItem>
        <DropdownMenuItem>Add Note</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="p-4 border-b border-border">
        <h3 className="font-semibold text-foreground">Active Matters</h3>
        <p className="text-sm text-muted-foreground">
          {activeMatterCount} active matters requiring attention
        </p>
      </div>
      {!matters || matters.length === 0 ? (
        <div className="p-8 text-center text-muted-foreground">
          <p>Your active property verifications and title searches will appear here. Start by reviewing a new deal intake or creating a matter.</p>
        </div>
      ) : isMobile ? (
        <div className="space-y-4 p-4">
          {matters.map((matter) => {
            const status = (matter.status as keyof typeof statusConfig) || 'active';
            return (
              <div
                key={matter.id}
                role="button"
                tabIndex={0}
                onClick={() => onViewMatter?.(matter.id)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onViewMatter?.(matter.id);
                  }
                }}
                className="w-full rounded-2xl border border-border bg-background p-4 text-left transition-colors hover:bg-muted/40 focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-1">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">{matter.matter_number}</p>
                    <h4 className="text-base font-semibold text-foreground">{matter.title}</h4>
                    <p className="text-sm text-muted-foreground">{matter.property_address}</p>
                  </div>
                  {renderActionMenu(matter.id)}
                </div>

                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-xl bg-muted/40 p-3">
                    <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Client</p>
                    <p className="mt-1 text-sm font-medium text-foreground">
                      <ClientNameCell matterId={matter.id} clientId={matter.client_id} />
                    </p>
                  </div>
                  <div className="rounded-xl bg-muted/40 p-3">
                    <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Current Stage</p>
                    <Badge variant="outline" className={`mt-2 ${stageBadgeClass}`}>
                      {getStageLabel(matter.current_stage)}
                    </Badge>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                  <Badge variant={statusConfig[status]?.variant || 'default'}>
                    {statusConfig[status]?.label || matter.status}
                  </Badge>
                  <p className="text-xs text-muted-foreground">
                    Updated {formatDistanceToNow(new Date(matter.updated_at), { addSuffix: true })}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Matter ID</TableHead>
              <TableHead>Property</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Current Stage</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Last Updated</TableHead>
              <TableHead className="w-[80px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {matters.map((matter) => {
              const status = (matter.status as keyof typeof statusConfig) || 'active';
              return (
                <TableRow
                  key={matter.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => onViewMatter?.(matter.id)}
                >
                  <TableCell className="font-medium text-primary">{matter.matter_number}</TableCell>
                  <TableCell>
                    <div>
                      <div className="font-medium">{matter.title}</div>
                      <div className="text-xs text-muted-foreground">{matter.property_address}</div>
                    </div>
                  </TableCell>
                  <TableCell><ClientNameCell matterId={matter.id} clientId={matter.client_id} /></TableCell>
                  <TableCell>
                    <Badge variant="outline" className={stageBadgeClass}>
                      {getStageLabel(matter.current_stage)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusConfig[status]?.variant || 'default'}>
                      {statusConfig[status]?.label || matter.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDistanceToNow(new Date(matter.updated_at), { addSuffix: true })}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(event) => {
                          event.stopPropagation();
                          onViewMatter?.(matter.id);
                        }}
                      >
                        <FileSearch className="mr-1 h-4 w-4" />
                        Start Due Diligence
                      </Button>
                      {renderActionMenu(matter.id)}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        </div>
      )}
    </div>
  );
}
